/**
 * Hook para gestionar las columnas de AG Grid en el módulo LocationOrders
 *
 * Responsabilidades:
 * - Definir las columnas (ColDef) con sus renderers personalizados
 * - Todas las columnas siempre visibles (sin selector de columnas)
 *
 * ⚠️ IMPORTANTE: NINGUNA columna debe ser sortable según requerimiento del usuario
 */
import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { LocationOrdersModel } from '../models/LocationOrders';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-light-svg-icons';
import { Link } from 'react-router-dom';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

// 🎯 Función para obtener el color del badge según el estado
const getStatusColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'done':
    case 'completo':
      return 'text-success light:bg-bg-success';
    case 'pending':
    case 'pendiente':
      return 'text-badge-status-3 light:bg-badge-bg-status-3';
    case 'in_progress':
    case 'en_proceso':
      return 'text-warning light:bg-bg-warning';
    case 'cancelled':
    case 'cancelado':
      return 'text-error light:bg-bg-error';
    default:
      return 'text-badge-status-3';
  }
};

// 🎯 Función para obtener el nombre del estado
const getStatusName = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'done':
      return 'Completo';
    case 'pending':
      return 'Pendiente';
    case 'in_progress':
      return 'En proceso';
    case 'cancelled':
      return 'Cancelado';
    default:
      return status || '-';
  }
};

// 🎯 Componente para la celda de estado
const StatusCell = ({ status }: { status: string }) => {
  const statusName = getStatusName(status);
  const statusColor = getStatusColor(status);

  return (
    <Badge variant="outline" className={`text-xs bg-card-border border-0 ${statusColor}`}>
      {statusName}
    </Badge>
  );
};

/**
 * Hook para gestionar las columnas de AG Grid
 *
 * ⚠️ CRÍTICO: NINGUNA columna tiene sortable: true según indicación del usuario
 * ⚠️ Patrón de Orders: Todas las columnas siempre visibles (sin ColumnSelector)
 */
export const useAgGridColumns = (selectedColumns: string[] = []) => {
  const { t } = useTranslation('locationordersModule');

  // 🎯 Definiciones completas de columnas para AG Grid
  // ⚠️ NINGUNA columna tiene sortable: true según requerimiento del usuario
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // 🔵 COLUMNA: ID / Código
      {
        field: 'id',
        headerName: t('table.id'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        // 🎯 CELDA: ID con formato
        cellRenderer: (params: any) => {
          const locationOrder = params.data as LocationOrdersModel;
          return <span className="text-sm font-medium text-title">#{locationOrder.id}</span>;
        },
      },

      // 🔵 COLUMNA: Carrito (cart)
      {
        field: 'cart_id',
        headerName: t('table.cart_id'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        // 🎯 CELDA: Cart code (nombre) en lugar de ID
        cellRenderer: (params: any) => {
          const locationOrder = params.data as LocationOrdersModel;
          return <span className="text-sm text-title">{locationOrder.cart?.code || '-'}</span>;
        },
      },

      // 🔵 COLUMNA: Tipo de orden
      {
        field: 'order_type',
        headerName: t('table.order_type'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        // 🎯 CELDA: Badge con tipo de orden
        cellRenderer: (params: any) => {
          const locationOrder = params.data as LocationOrdersModel;
          const orderType = locationOrder.order_type;

          const getOrderTypeLabel = (type: string): string => {
            switch (type?.toLowerCase()) {
              case 'reused':
                return 'Rehusados';
              case 'picking_return':
                return 'Devolución picking';
              case 'entry':
                return 'Mercancía';
              default:
                return type || '-';
            }
          };

          const getOrderTypeColor = (type: string): string => {
            switch (type?.toLowerCase()) {
              case 'reused':
                return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950';
              case 'picking_return':
                return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950';
              case 'entry':
                return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950';
              default:
                return 'text-muted-foreground bg-muted';
            }
          };

          return (
            <Badge variant="outline" className={`text-xs border-0 ${getOrderTypeColor(orderType)}`}>
              {getOrderTypeLabel(orderType)}
            </Badge>
          );
        },
      },

      // 🔵 COLUMNA: Estado
      {
        field: 'status',
        headerName: t('table.status'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        valueGetter: (params) => params.data.status,
        // 🎯 CELDA: Badge con color según estado
        cellRenderer: (params: any) => {
          const locationOrder = params.data as LocationOrdersModel;
          return <StatusCell status={locationOrder.status} />;
        },
      },

      // 🔵 COLUMNA: Fecha de creación
      {
        field: 'created_at',
        headerName: t('table.created_at'),
        sortable: false, // ⚠️ NO SORTABLE según usuario
        // 🎯 CELDA: Fecha formateada
        cellRenderer: (params: any) => {
          const locationOrder = params.data as LocationOrdersModel;
          return <span className="text-sm">{formatDate(locationOrder.created_at)}</span>;
        },
      },
    ];

    // 🔵 COLUMNA: Acciones (siempre presente)
    baseColumns.push({
      headerName: '',
      field: 'actions',
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right', // Evita que el usuario mueva la columna
      suppressMovable: true, // Deshabilita el drag & drop para esta columna
      suppressSizeToFit: true,
      suppressAutoSize: true,
      sortable: false, // ⚠️ NO SORTABLE según usuario
      // 🎯 CELDA: Botón de ver detalles
      cellRenderer: (params: any) => {
        const locationOrder = params.data as LocationOrdersModel;

        return (
          <div className="flex justify-center w-full">
            {/* Enlace real: Ctrl/Cmd+clic o clic medio abren el detalle en nueva pestaña - Real link for new-tab modifiers */}
            <Button variant="ghost" size="sm" asChild title="Ver detalles">
              <Link
                to={`/location-orders/${locationOrder.id}`}
                data-testid="view-details-button"
                onClick={(e) => e.stopPropagation()}
              >
                <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        );
      },
    });

    return baseColumns;
  }, [t]);

  // 🎯 CRÍTICO: Filtrar columnas basado en selectedColumns (sin ColumnSelector)
  const filteredColumnDefs: ColDef[] = useMemo(() => {
    // dYZ_ Si no hay selectedColumns, devolver todas las columnas
    if (selectedColumns.length === 0) {
      return allColumnDefs;
    }

    const alwaysVisibleFields = new Set(['actions']);

    // dYZ_ Filtrar columnas basado en selectedColumns, manteniendo las fijas
    return allColumnDefs.filter((col) => {
      const fieldId = col.field || col.headerName || '';
      if (!fieldId) {
        return false;
      }

      if (alwaysVisibleFields.has(fieldId)) {
        return true;
      }

      return selectedColumns.includes(fieldId) || selectedColumns.includes(col.headerName || '');
    });
  }, [allColumnDefs, selectedColumns]);

  // 🎯 Convertir a formato tanStack para ColumnSelector
  const tanStackColumns = useMemo<ColumnDef<LocationOrdersModel>[]>(() => {
    return allColumnDefs
      .filter((col) => {
        const fieldId = col.field || col.headerName || '';
        return fieldId && fieldId !== 'actions';
      })
      .map((col) => {
        const fieldId = col.field || col.headerName || '';
        return {
          id: fieldId,
          accessorKey: fieldId,
          header: col.headerName || fieldId,
        } as ColumnDef<LocationOrdersModel>;
      });
  }, [allColumnDefs]);

  // 🎯 Devolver columnas (todas siempre visibles)
  return {
    columnDefs: filteredColumnDefs,
    tanStackColumns,
  };
};
